import { useState } from "react";
import { Icon } from "./Icon.js";
import { Menu } from "@vcnet/ui";

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  /** How many items it would show, when the list knows. */
  count?: number;
}

/** Past this many choices the menu gets a box to narrow them by typing. */
const NARROW_FROM = 8;

/**
 * One filter, as one chip: "Category" until something is picked, then the
 * thing picked ("Tools"), tinted, with the choices a tap away. Replaces the
 * Filters button and its panel of selects, which took two taps to see what
 * was on offer and a third to see what was already on, and said the same
 * thing twice as a select and as a chip.
 *
 * `anyLabel` is the way back to "not filtered", always the first row. The
 * menu is `Menu`, so on a phone it is a sheet from the bottom edge.
 */
export function FilterChip<T extends string>({
  label,
  value,
  options,
  anyLabel = "Any",
  onChange,
}: {
  /** The facet's name: what the chip says while nothing is picked. */
  label: string;
  value: T | undefined;
  options: readonly FilterOption<T>[];
  anyLabel?: string;
  onChange: (value: T | undefined) => void;
}) {
  const [typed, setTyped] = useState("");
  const picked = options.find((option) => option.value === value);
  const needle = typed.trim().toLowerCase();
  const shown = needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;

  const row = (key: string, text: string, on: boolean, pick: () => void, count?: number) => (
    <button
      key={key}
      type="button"
      role="menuitemradio"
      aria-checked={on}
      className={on ? "menu-item filter-option filter-option-on" : "menu-item filter-option"}
      onClick={pick}
    >
      <span className="filter-option-mark">{on && <Icon name="check" size={16} />}</span>
      <span className="filter-option-label">{text}</span>
      {count !== undefined && <span className="filter-option-count">{count}</span>}
    </button>
  );

  return (
    <Menu
      label={picked ? `${label}: ${picked.label}. Change` : `Filter by ${label.toLowerCase()}`}
      align="start"
      buttonClassName={picked ? "chip chip-on filter-chip" : "chip filter-chip"}
      button={
        <>
          <span className="filter-chip-text">{picked ? picked.label : label}</span>
          <Icon name="chevron-down" size={14} />
        </>
      }
    >
      {(close) => (
        <div className="filter-menu" role="menu" aria-label={label}>
          <p className="filter-menu-title">{label}</p>
          {options.length > NARROW_FROM && (
            <input
              className="input input-small filter-menu-search"
              aria-label={`Find a ${label.toLowerCase()}`}
              placeholder="Type to narrow"
              autoFocus
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          )}
          <div className="filter-menu-list">
            {!needle &&
              row("", anyLabel, value === undefined, () => {
                onChange(undefined);
                close();
              })}
            {shown.map((option) =>
              row(
                option.value,
                option.label,
                option.value === value,
                () => {
                  onChange(option.value);
                  setTyped("");
                  close();
                },
                option.count,
              ),
            )}
            {shown.length === 0 && <p className="filter-menu-empty">Nothing called that.</p>}
          </div>
        </div>
      )}
    </Menu>
  );
}

/** An on-or-off filter ("Needs a name") as a chip that tints when on. */
export function FilterToggle({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={on ? "chip chip-on filter-chip" : "chip filter-chip"}
      aria-pressed={on}
      onClick={() => onChange(!on)}
    >
      {on && <Icon name="check" size={14} />}
      {label}
    </button>
  );
}
